import { FC, useContext, useEffect, useState } from "react";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { useMutation } from "react-query";
import client from "../../api";
import { BankCard, IMTS, PaginationInput, User } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
} from "src/lib/constants";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  convertChannelName,
  convertPaymentSystem,
  extractBillingStatus,
  extractServiceName,
  formatCardNumber,
  getCurrencyInfo,
  getErrorMessage,
  getTransactionStatusStyle,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  BalanceDataType,
  BankCardDataType,
  IBillingTransaction,
  IMTSTable,
  TransactionDataType,
} from "src/lib/constants/table-type";
import { useModalAction } from "../modal-views/context";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import routes from "src/config/routes";
import SafeImage from "src/hooks/validateImageUrl";

type TransactionsCashoutPageProps = {
  user: User;
  onSelectedInvoiceData: any;
};

const TransactionsMtsPage: FC<TransactionsCashoutPageProps> = ({
  user,
  onSelectedInvoiceData,
}) => {
  const navigate = useNavigate();
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { openModal } = useModalAction();

  const [dataSource, setDataSource] = useState<IMTS[]>([]);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });

  const [total, setTotal] = useState(0);

  useEffect(() => {
    isMounted && getList({ id: user.id, params: searchParams });
  }, [isMounted, searchParams.current_page]);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({ ...searchParams, current_page: page });
  };

  // const onSelectUser = (userID: number) => {
  //   navigate(`/${isBuisiness ? "users-business" : "users"}/${String(userID)}`, {
  //     replace: true,
  //   });
  // };

  const { mutate: getList, isLoading } = useMutation(
    user.is_business
      ? client.users.getMtsBuisinessTransactions
      : client.users.getMtsBuisinessTransactions,
    {
      onSuccess: (data: any) => {
        setTotal(data?.total);

        setDataSource(
          data.data.map((item: any) => ({
            key: item.id,
            id: item.id,
            request_id: item.request_id,
            channel: item.channel,
            created_at: item.created_at,
            updated_at: item.updated_at,
            sender_full_name: `${item.sender_first_name} ${item.sender_last_name}`,
            sender_dob: item.sender_dob,
            sender_personal_number: item.sender_personal_number,
            sender_currency: item.sender_currency,
            sender_amount: item.sender_amount,
            sender_bank_code: item.sender_bank_code,
            sender_bank_name: item.sender_bank_name,
            receiver_full_name: `${item.receiver_first_name} ${item.receiver_last_name}`,
            receiver_dob: item.receiver_dob,
            receiver_personal_number: item.receiver_personal_number,
            receiver_currency: item.receiver_currency,
            receiver_iban: item.receiver_iban,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const onSelectUser = (userID: number) => {
    navigate(
      `/${user?.is_business ? "users-business" : "users"}/${String(userID)}`,
      {
        replace: true,
      }
    );
  };

  const onViewInvoice = (key: number) => {
    const filterInvoices = dataSource.filter((item) => item.key === key);
    if (filterInvoices.length === 0) {
      return;
    }

    const selInvoice = filterInvoices[0];
    onSelectedInvoiceData(selInvoice);
    /*
    setInvoiceFromPage(routes.users);
    selInvoice.invoice_data &&
      navigate(routes.invoice, { state: { info: selInvoice } });
    */
  };

  const changeName = (name: string) => {
    return name === "cartubank" ? "Card" : name;
  };

  const columns: ColumnsType<IMTSTable> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      className: "w-auto",
      key: "id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Request ID
        </p>
      ),
      dataIndex: "request_id",
      className: "w-auto",
      key: "request_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Channel
        </p>
      ),
      dataIndex: "channel",
      className: "w-auto",
      key: "channel",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Name
        </p>
      ),
      dataIndex: "sender_full_name",
      className: "w-auto",
      key: "sender_full_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender DOB
        </p>
      ),
      dataIndex: "sender_dob",
      className: "w-auto",
      key: "sender_dob",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Personal Number
        </p>
      ),
      dataIndex: "sender_personal_number",
      className: "w-auto",
      key: "sender_personal_number",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Currency
        </p>
      ),
      dataIndex: "sender_currency",
      className: "w-auto",
      key: "sender_currency",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value && capitalizeFirstLetter(value.replaceAll("_", " "))}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "sender_amount",
      className: "w-auto",
      key: "sender_amount",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Bank Code
        </p>
      ),
      dataIndex: "sender_bank_code",
      className: "w-auto",
      key: "sender_bank_code",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Bank Name
        </p>
      ),
      dataIndex: "sender_bank_name",
      className: "w-auto",
      key: "sender_bank_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Name
        </p>
      ),
      dataIndex: "receiver_full_name",
      className: "w-auto",
      key: "receiver_full_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver DOB
        </p>
      ),
      dataIndex: "receiver_dob",
      className: "w-auto",
      key: "receiver_dob",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Personal Number
        </p>
      ),
      dataIndex: "receiver_personal_number",
      className: "w-auto",
      key: "receiver_personal_number",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Currency
        </p>
      ),
      dataIndex: "receiver_currency",
      className: "w-auto",
      key: "receiver_currency",
      render: (value) => {
        return (
          <div
            className={`bg-[${
              value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
            }] inline-block py-[4px] px-[12px] rounded-[10px]`}
          >
            <p
              className={`font-manrope ${
                value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
              } text-[12px] font-medium leading-none`}
            >
              {value && capitalizeFirstLetter(value.replaceAll("_", " "))}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Iban
        </p>
      ),
      dataIndex: "receiver_iban",
      className: "w-auto",
      key: "receiver_iban",
      render: (value) => {
        return (
          <div
            className={`bg-[${
              value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
            }] inline-block py-[4px] px-[12px] rounded-[10px]`}
          >
            <p
              className={`font-manrope ${
                value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
              } text-[12px] font-medium leading-none`}
            >
              {value && capitalizeFirstLetter(value.replaceAll("_", " "))}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {/* {moment(value).format("YYYY-MM-DD HH:mm:ss")} */}
            {moment(value).utcOffset(240).format("DD/MM/YYYY H:mm")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Updated
        </p>
      ),
      dataIndex: "updated_at",
      className: "w-auto",
      key: "updated_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {/* {moment(value).format("YYYY-MM-DD HH:mm:ss")} */}
            {moment(value).utcOffset(240).format("DD/MM/YYYY H:mm")}
          </p>
        </div>
      ),
    },
  ];

  const mobileColumns: ColumnsType<IMTSTable> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          MTS
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-row gap-2 justify-between">
            <div className="w-full flex flex-col items-start gap-2">
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  ID
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.id}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Request ID
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.request_id || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Channel
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.channel || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Name
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_full_name || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Currency
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData &&
                    capitalizeFirstLetter(
                      selData.sender_currency.replaceAll("_", " ")
                    )}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender DOB
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_dob}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Personal Number
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_personal_number}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Currency
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_currency}
                </p>
              </div>
            </div>

            <div className="w-full flex flex-col items-start gap-2">
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Amount
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_amount || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Bank Code
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_bank_code || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Bank Name
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_bank_name || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver Name
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_full_name || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver DOB
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_dob || "---"}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver Personal Number
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_personal_number || "---"}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver Currency
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_currency &&
                    capitalizeFirstLetter(
                      selData.receiver_currency.replaceAll("_", " ")
                    )}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver Iban
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_iban &&
                    capitalizeFirstLetter(
                      selData.receiver_iban.replaceAll("_", " ")
                    )}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Created
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {/* {moment(selData.created_at).format("YYYY-MM-DD HH:mm:ss")} */}
                  {moment
                    .utc(selData.created_at)
                    .utcOffset(240)
                    .format("DD/MM/YYYY H:mm")}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Updated
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {/* {moment(selData.created_at).format("YYYY-MM-DD HH:mm:ss")} */}
                  {moment
                    .utc(selData.updated_at)
                    .utcOffset(240)
                    .format("DD/MM/YYYY H:mm")}
                </p>
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="w-full">
      <div className="p-2 relative">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={isMobile ? mobileColumns : columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.current_page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default TransactionsMtsPage;
